import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, BedDouble, ShieldCheck, Star, Crown, ScanLine, ClipboardList, Lock, Smile, Check } from 'lucide-react';
import './MaidServicesSpecialOfferPage.css';

const MaidServicesSpecialOfferPage = () => {
  const scrollToServices = (e) => {
    e.preventDefault();
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="mso-page" data-testid="maid-special-offer-page">
      {/* Hero */}
      <section className="mso-hero">
        <div className="container">
          <p className="mso-eyebrow">LAVA &amp; CO — LUXURY PROPERTY CARE</p>
          <h1 className="mso-hero-title font-heading" data-testid="mso-hero-title">
            Enjoy Your Stay.
            <br />
            <span className="mso-hero-accent">We&apos;ll Handle the Rest.</span>
          </h1>
          <p className="mso-hero-subtitle">
            Need a fresh clean during your vacation? Our professional team will have your space
            feeling brand new — so you can focus on enjoying your trip.
          </p>
          <a href="#services" onClick={scrollToServices} className="btn btn-primary btn-large" data-testid="mso-view-services-cta">
            View Services &amp; Book
          </a>
          <div className="mso-hero-trust">
            <div className="mso-trust-item"><ShieldCheck size={18} /> Licensed &amp; Insured</div>
            <div className="mso-trust-item"><Star size={18} /> 5-Star Rated</div>
            <div className="mso-trust-item"><Crown size={18} /> Luxury Standards</div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="mso-services">
        <div className="container">
          <p className="mso-section-eyebrow">OUR SERVICES</p>
          <h2 className="mso-section-title font-heading">Choose Your Service</h2>
          <p className="mso-section-subtitle">Simple, transparent pricing — book in under 2 minutes</p>

          <div className="mso-cards">
            {/* Standard Maid */}
            <div className="mso-card" data-testid="mso-card-standard">
              <div className="mso-card-head">
                <Sparkles size={32} strokeWidth={1.6} />
                <div>
                  <h3 className="mso-card-title">Standard Maid Service</h3>
                  <p className="mso-card-duration">2–4 hours</p>
                </div>
              </div>
              <div className="mso-card-price">
                <span className="mso-price-label">Starting from</span>
                <span className="mso-price-amount">$220</span>
              </div>
              <ul className="mso-card-list">
                <li><Check size={16} /> Clean all common areas</li>
                <li><Check size={16} /> Kitchen wipe-down (no oven/fridge)</li>
                <li><Check size={16} /> Bathrooms fully cleaned</li>
                <li><Check size={16} /> Sweep &amp; mop all floors</li>
                <li><Check size={16} /> Dust all surfaces</li>
                <li><Check size={16} /> Remove trash &amp; tidy</li>
                <li><Check size={16} /> Personal Laundry <span className="mso-addon-tag">+$100</span></li>
              </ul>
              <p className="mso-card-note">A quick reset to keep your space fresh during your stay</p>
              <Link
                to="/book?service=maid_standard&src=maid-special-offer"
                className="btn btn-primary mso-card-cta"
                data-testid="mso-book-standard"
              >
                Book Now — $220
              </Link>
            </div>

            {/* Maid + Linen (Most Popular) */}
            <div className="mso-card mso-card-featured" data-testid="mso-card-linen">
              <span className="mso-badge">Most Popular</span>
              <div className="mso-card-head">
                <BedDouble size={32} strokeWidth={1.6} />
                <div>
                  <h3 className="mso-card-title">Maid Service &amp; Linen Change</h3>
                  <p className="mso-card-duration">4–5 hours</p>
                </div>
              </div>
              <div className="mso-card-price">
                <span className="mso-price-label">Starting from</span>
                <span className="mso-price-amount">$280</span>
              </div>
              <ul className="mso-card-list">
                <li><Check size={16} /> Everything in Standard Maid Service</li>
                <li><Check size={16} /> Full linen &amp; towel change</li>
                <li><Check size={16} /> Wash &amp; fold used linens on-site</li>
                <li><Check size={16} /> Freshly made beds with crisp sheets</li>
                <li><Check size={16} /> Personal Laundry <span className="mso-addon-tag">+$100</span></li>
              </ul>
              <p className="mso-card-note">Like checking in all over again — hotel-quality comfort</p>
              <Link
                to="/book?service=maid_linen&src=maid-special-offer"
                className="btn btn-primary mso-card-cta"
                data-testid="mso-book-linen"
              >
                Book Now — $280
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mso-how">
        <div className="container">
          <p className="mso-section-eyebrow">HOW IT WORKS</p>
          <h2 className="mso-section-title font-heading">Scan. Book. Done.</h2>

          <div className="mso-steps">
            <div className="mso-step">
              <div className="mso-step-num">1</div>
              <ScanLine size={26} strokeWidth={1.6} />
              <h4>Pick Your Service</h4>
              <p>Choose from maid service or linen change</p>
            </div>
            <div className="mso-step">
              <div className="mso-step-num">2</div>
              <ClipboardList size={26} strokeWidth={1.6} />
              <h4>Enter Your Details</h4>
              <p>Property address, access info, and when you&apos;d like us there</p>
            </div>
            <div className="mso-step">
              <div className="mso-step-num">3</div>
              <Lock size={26} strokeWidth={1.6} />
              <h4>Pay Securely</h4>
              <p>Fixed price, no surprises — pay online in seconds</p>
            </div>
            <div className="mso-step">
              <div className="mso-step-num">4</div>
              <Smile size={26} strokeWidth={1.6} />
              <h4>Enjoy Your Stay</h4>
              <p>We handle the rest — come back to a spotless space</p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="mso-additional">
        <div className="container">
          <p className="mso-section-eyebrow">NEED MORE?</p>
          <h2 className="mso-section-title font-heading">Additional Services</h2>

          <div className="mso-additional-grid">
            <div className="mso-additional-card">
              <h4>Full / Deep Cleaning</h4>
              <p>A thorough top-to-bottom clean — inside appliances, baseboards, windows, and every detail. Perfect if the space needs extra love.</p>
              <span className="mso-additional-price">Custom Quote</span>
            </div>
            <div className="mso-additional-card">
              <h4>Post-Event Reset</h4>
              <p>Had a dinner party or get-together during your stay? We&apos;ll handle the cleanup so you don&apos;t have to.</p>
              <span className="mso-additional-price">Custom Quote</span>
            </div>
          </div>
          <div className="mso-additional-cta">
            <Link to="/book" className="btn btn-secondary" data-testid="mso-custom-quote-cta">
              Get a Custom Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mso-final-cta">
        <div className="container">
          <p className="mso-eyebrow mso-eyebrow-light">LAVA &amp; CO — LUXURY PROPERTY CARE</p>
          <h2 className="mso-final-title font-heading">Life&apos;s messy. Your home doesn&apos;t have to be.</h2>
          <p className="mso-final-subtitle">Book your cleaning today and get back to enjoying your trip.</p>
          <a href="#services" onClick={scrollToServices} className="btn btn-primary btn-large" data-testid="mso-final-cta">
            Book Your Cleaning
          </a>
        </div>
      </section>
    </div>
  );
};

export default MaidServicesSpecialOfferPage;
